// @ts-check
// Configurações > Contas conectadas (doc 03 do mapeamento, seção 5; contrato 08 da agenda, seção 6): conectar Google
// ou Microsoft 365 por OAuth, ver o estado da sincronização, escolher o que a equipe vê dos e-mails e da agenda,
// sincronizar agora e desconectar.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { CALENDAR_VISIBILITIES, MESSAGE_VISIBILITIES } from '../../../../shared/calendar/event-types.js';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import {
  disconnectAccount, listConnectedAccounts, startAccountConnection, syncConnectedAccountNow, updateConnectedAccount,
} from '../../data/calendar.js';
import { sessionState } from '../../data/metadata-store.js';
import { formatRelativeToNow } from '../../lib/format.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import { BlocklistSection } from '../email/blocklist-section.js';
import { ContactCreationOptions } from '../email/contact-creation-options.js';

/** @typedef {import('../../data/calendar.js').ConnectedAccount} ConnectedAccount */

const PROVIDER_ICONS = /** @type {Record<string, string>} */ ({ GOOGLE: 'IconBrandGoogle', MICROSOFT: 'IconLayoutGrid' });
const STATUS_TONES = /** @type {Record<string, string>} */ ({ ACTIVE: 'success', ONGOING: 'pending', NOT_SYNCED: 'pending', FAILED_INSUFFICIENT_PERMISSIONS: 'danger', FAILED_UNKNOWN: 'danger' });

/** @param {'google' | 'microsoft'} provider @param {{ calendarEvents?: boolean, bookingHost?: boolean }} [options] */
async function connect(provider, options = {}) {
  try {
    const authorizationUrl = await startAccountConnection(provider, options);
    // o modo demonstração devolve um caminho local; o real, a tela de consentimento do provedor
    if (authorizationUrl.startsWith('/')) navigate(authorizationUrl);
    else window.location.assign(authorizationUrl);
  } catch (error) {
    showErrorSnackbar(error);
  }
}

/**
 * @param {{ name: string, options: readonly string[], value: string, labelPrefix: string, disabled: boolean, onChange: (value: string) => void }} props
 */
function VisibilityOptions({ name, options, value, labelPrefix, disabled, onChange }) {
  return html`<div class="account-visibility-options" role="radiogroup" aria-label=${t(`${labelPrefix}.title`)}>
    ${options.map((option) => html`<label key=${option} class="account-visibility-option" data-selected=${value === option ? 'true' : undefined} data-disabled=${disabled ? 'true' : undefined}>
      <input type="radio" name=${name} value=${option} checked=${value === option} disabled=${disabled} onChange=${() => onChange(option)} />
      <span class="account-visibility-option-label">${t(`${labelPrefix}.${option}.label`)}</span>
      <span class="account-visibility-option-description">${t(`${labelPrefix}.${option}.description`)}</span>
    </label>`)}
  </div>`;
}

/**
 * @param {{ status: string, label: string, syncedAt: string | null }} props
 */
function SyncStatus({ status, label, syncedAt }) {
  return html`<div class="account-sync-status" data-tone=${STATUS_TONES[status] ?? 'pending'}>
    <span class="account-sync-status-dot"></span>
    <span class="account-sync-status-label">${label}</span>
    <span class="account-sync-status-text">${t(`calendar.accounts.status.${status}`)}</span>
    ${syncedAt ? html`<span class="account-sync-status-time">${t('calendar.accounts.lastSynced', { relative: formatRelativeToNow(syncedAt) })}</span>` : null}
  </div>`;
}

/**
 * @param {{ account: ConnectedAccount, canManage: boolean, onChanged: (account?: ConnectedAccount) => void, onRemoved: () => void }} props
 */
function AccountCard({ account, canManage, onChanged, onRemoved }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const needsReconnect = Boolean(account.authFailedAt);
  // contas conectadas antes das páginas de agendamento (3.0.0) só leem a agenda: reconectar dá a permissão de criar eventos
  const needsEventsPermission = account.provider === 'GOOGLE' && account.canCreateEvents === false;

  /** @param {Partial<ConnectedAccount>} patch */
  const save = async (patch) => {
    try {
      onChanged(await updateConnectedAccount(account.id, patch));
      showSuccessSnackbar(t('calendar.accounts.savedSnackbar'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const syncNow = async () => {
    setIsSyncing(true);
    try {
      const { account: updated } = await syncConnectedAccountNow(account.id);
      onChanged(updated);
      showSuccessSnackbar(t('calendar.accounts.syncedSnackbar'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSyncing(false);
    }
  };

  const remove = async () => {
    setIsRemoving(true);
    try {
      await disconnectAccount(account.id);
      showSuccessSnackbar(t('calendar.accounts.disconnectedSnackbar'));
      setIsConfirmingRemoval(false);
      onRemoved();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsRemoving(false);
    }
  };

  return html`<div class="channel-card account-card" data-testid="connected-account-card">
    <div class="channel-card-header">
      <span class="account-card-provider"><${Icon} name=${PROVIDER_ICONS[account.provider] ?? 'IconMail'} size=${20} /></span>
      <div class="channel-card-titles">
        <span class="channel-card-title">${account.handle}</span>
        <span class="channel-card-description">
          ${t(`calendar.accounts.providers.${account.provider}`)} · ${account.isMine ? t('calendar.accounts.mine') : t('calendar.accounts.ownerLabel', { name: account.ownerName ?? '' })}
        </span>
      </div>
    </div>
    ${needsReconnect
      ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t('calendar.accounts.status.FAILED_INSUFFICIENT_PERMISSIONS')}</span></div>`
      : null}
    ${!needsReconnect && needsEventsPermission
      ? html`<div class="account-card-notice" role="status" data-testid="account-events-permission">
          <${Icon} name="IconCalendarPlus" size=${16} />
          <div><strong>${t('calendar.accounts.calendarWriteMissing')}</strong><span>${t('calendar.accounts.calendarWriteMissingHint')}</span></div>
          ${account.isMine ? html`<${Button} title=${t('calendar.accounts.reconnectForEvents')} icon="IconRefresh" accent="blue" size="small" onClick=${() => connect('google', { calendarEvents: true })} />` : null}
        </div>`
      : null}
    <div class="account-card-statuses">
      <${SyncStatus} status=${account.isMessageSyncEnabled ? account.messageSyncStatus : 'NOT_SYNCED'} label=${t('calendar.accounts.messages')} syncedAt=${account.messageSyncedAt} />
      <${SyncStatus} status=${account.isCalendarSyncEnabled ? account.calendarSyncStatus : 'NOT_SYNCED'} label=${t('calendar.accounts.calendar')} syncedAt=${account.calendarSyncedAt} />
    </div>
    <div class="account-card-section">
      <div class="account-card-toggle-row">
        <span>${t('calendar.accounts.syncMessages')}</span>
        ${canManage ? html`<${Toggle} value=${account.isMessageSyncEnabled} onChange=${(value) => save({ isMessageSyncEnabled: value })} ariaLabel=${t('calendar.accounts.syncMessages')} />` : null}
      </div>
      <span class="form-field-label">${t('calendar.accounts.messageVisibility.title')}</span>
      <${VisibilityOptions}
        name=${`message-visibility-${account.id}`}
        options=${MESSAGE_VISIBILITIES}
        value=${account.messageVisibility}
        labelPrefix="calendar.accounts.messageVisibility"
        disabled=${!canManage}
        onChange=${(value) => save({ messageVisibility: /** @type {any} */ (value) })}
      />
    </div>
    <${ContactCreationOptions} account=${account} canManage=${canManage} onSave=${save} />
    <div class="account-card-section">
      <div class="account-card-toggle-row">
        <span>${t('calendar.accounts.syncCalendar')}</span>
        ${canManage ? html`<${Toggle} value=${account.isCalendarSyncEnabled} onChange=${(value) => save({ isCalendarSyncEnabled: value })} ariaLabel=${t('calendar.accounts.syncCalendar')} />` : null}
      </div>
      <span class="form-field-label">${t('calendar.accounts.calendarVisibility.title')}</span>
      <${VisibilityOptions}
        name=${`calendar-visibility-${account.id}`}
        options=${CALENDAR_VISIBILITIES}
        value=${account.calendarVisibility}
        labelPrefix="calendar.accounts.calendarVisibility"
        disabled=${!canManage}
        onChange=${(value) => save({ calendarVisibility: /** @type {any} */ (value) })}
      />
    </div>
    ${canManage
      ? html`<div class="channel-card-actions">
          ${needsReconnect && account.isMine
            ? html`<${Button} title=${t('calendar.accounts.reconnect')} icon="IconRefresh" accent="blue" size="small" onClick=${() => connect(account.provider === 'GOOGLE' ? 'google' : 'microsoft')} />`
            : html`<${Button} title=${t('calendar.accounts.syncNow')} icon="IconRefresh" variant="secondary" size="small" isLoading=${isSyncing} onClick=${syncNow} />`}
          <${Button} title=${t('calendar.accounts.disconnect')} icon="IconUnlink" variant="secondary" accent="danger" size="small" onClick=${() => setIsConfirmingRemoval(true)} />
        </div>`
      : html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('calendar.accounts.readOnlyHint')}</p>`}
    ${isConfirmingRemoval
      ? html`<${Modal}
          title=${t('calendar.accounts.disconnectTitle', { handle: account.handle })}
          subtitle=${t('calendar.accounts.disconnectSubtitle')}
          confirmLabel=${t('calendar.accounts.disconnect')}
          confirmAccent="danger"
          isLoading=${isRemoving}
          onClose=${() => setIsConfirmingRemoval(false)}
          onConfirm=${remove}
        />`
      : null}
  </div>`;
}

export function SettingsAccountsPage() {
  const [data, setData] = useState(/** @type {Awaited<ReturnType<typeof listConnectedAccounts>> | null} */ (null));
  const isAdmin = sessionState.value?.role === 'ADMIN';

  const load = () => {
    listConnectedAccounts()
      .then(setData)
      .catch((error) => {
        showErrorSnackbar(error);
        setData({ accounts: [], providers: { GOOGLE: { isConfigured: false }, MICROSOFT: { isConfigured: false } } });
      });
  };

  const connected = searchParamsState.value.get('connected');
  const connectionError = searchParamsState.value.get('error');
  const connectedAsHost = searchParamsState.value.get('host') === '1';
  // "Vou receber famílias pelas páginas de agendamento" (3.5.0): a conexão já pede criar eventos e grava o aceite
  const [isBookingHost, setIsBookingHost] = useState(false);
  useEffect(() => {
    document.title = `${t('calendar.accounts.title')} - ${t('navigation.settings')}`;
  }, []);
  // volta do OAuth: ?connected=GOOGLE ou ?error=ACCESS_DENIED (e &host=1 quando a conexão já veio como anfitrião)
  useEffect(() => {
    if (connected) showSuccessSnackbar(t(connectedAsHost ? 'calendar.accounts.connectedAsHostSnackbar' : 'calendar.accounts.connectedSnackbar', { provider: t(`calendar.accounts.providers.${connected}`) }));
    if (connectionError) enqueueSnackbar({ message: t(`calendar.accounts.errors.${connectionError === 'ACCESS_DENIED' ? 'ACCESS_DENIED' : 'OAUTH_FAILED'}`), variant: 'error' });
    if (connected || connectionError) updateSearchParams({ connected: null, error: null, provider: null, host: null });
    load();
  }, [connected, connectionError]);

  /** @param {ConnectedAccount | undefined} updated */
  const replaceAccount = (updated) => {
    if (!updated || !data) return load();
    setData({ ...data, accounts: data.accounts.map((account) => (account.id === updated.id ? updated : account)) });
  };

  const providerButton = (/** @type {'GOOGLE' | 'MICROSOFT'} */ provider) => {
    const isConfigured = data?.providers[provider]?.isConfigured ?? false;
    return html`<div class="account-connect-option">
      <${Button}
        title=${provider === 'GOOGLE' ? t('calendar.accounts.connectGoogle') : t('calendar.accounts.connectMicrosoft')}
        icon=${PROVIDER_ICONS[provider]}
        variant="secondary"
        size="small"
        disabled=${!isConfigured}
        onClick=${() => connect(provider === 'GOOGLE' ? 'google' : 'microsoft', provider === 'GOOGLE' && isBookingHost ? { calendarEvents: true, bookingHost: true } : {})}
      />
      ${data && !isConfigured ? html`<span class="input-hint">${t('calendar.accounts.notConfigured', { provider: t(`calendar.accounts.providers.${provider}`) })}</span>` : null}
    </div>`;
  };
  const googleIsConfigured = data?.providers.GOOGLE?.isConfigured ?? false;

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconAt" title=${t('calendar.accounts.title')} />`}>
    <div class="settings-page" data-testid="settings-accounts-page">
      <${SettingsSection} title=${t('calendar.accounts.title')} description=${t('calendar.accounts.description')}>
        <div class="account-connect-row">${providerButton('GOOGLE')}${providerButton('MICROSOFT')}</div>
        ${googleIsConfigured
          ? html`<label class="account-host-option" data-testid="connect-as-host">
              <${Checkbox} checked=${isBookingHost} onChange=${setIsBookingHost} ariaLabel=${t('calendar.accounts.connectAsHost')} />
              <span class="account-host-option-texts">
                <span>${t('calendar.accounts.connectAsHost')}</span>
                <span class="input-hint">${t('calendar.accounts.connectAsHostHint')}</span>
              </span>
            </label>`
          : null}
        <ul class="channel-card-explanation">
          <li><${Icon} name="IconUsers" size=${16} /><span>${t('calendar.accounts.explanation.onlyCrm')}</span></li>
          <li><${Icon} name="IconCornerUpLeft" size=${16} /><span>${t('calendar.accounts.explanation.reply')}</span></li>
          <li><${Icon} name="IconShieldLock" size=${16} /><span>${t('calendar.accounts.explanation.privacy')}</span></li>
        </ul>
        ${data === null
          ? html`<${Skeleton} height=${200} radius=${8} />`
          : data.accounts.length === 0
            ? html`<p class="channel-card-description">${t('calendar.accounts.empty')}</p>`
            : data.accounts.map((account) => html`<${AccountCard}
                key=${account.id}
                account=${account}
                canManage=${account.isMine || isAdmin}
                onChanged=${replaceAccount}
                onRemoved=${load}
              />`)}
      <//>
      <${SettingsSection} title=${t('emailAdvanced.blocklist.title')} description=${t('emailAdvanced.blocklist.description')}>
        <${BlocklistSection} />
      <//>
    </div>
  <//>`;
}
